import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiRefreshCw } from "react-icons/fi";
import useAuth from "../../Hooks/useAuth";
import { useLazyGetUserActivityQuery } from "../../store";
import { EmptyState, MascotLoadingOverlay } from "../shared";
import ActivityItem from "./ActivityItem";
import { appendActivityPage, groupActivityByDay } from "../../utils/userActivity";

const PAGE_SIZE = 25;

/** İlk yükleme iskeleti — kural gereği MascotLoadingOverlay üstte */
function ActivitySkeleton() {
  return (
    <div className="relative">
      <MascotLoadingOverlay message="Hareketler yükleniyor..." />
      <ul className="space-y-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <li key={i} className="flex gap-3">
            <span className="h-9 w-9 shrink-0 rounded-xl bg-gray-100 motion-safe:animate-pulse" />
            <span className="h-14 flex-1 rounded-xl bg-gray-100 motion-safe:animate-pulse" />
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Organizma: kullanıcı son hareketleri (#236) — yeniden eskiye zaman çizelgesi,
 * aşağı kaydırdıkça daha eskileri yüklenir (sunucu imleci `nextCursor`).
 * Sözleşme: Workspace docs/api-contracts/user-activity.md
 */
export default function UserActivityTab({ userId }) {
  const { auth } = useAuth();
  const [fetchPage] = useLazyGetUserActivityQuery();
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  // İmleç ve "devamı var" ref'te: loadMore bunları her çağrıda GÜNCEL okur.
  // State'ten okusaydı, gözlemci yenilenmeden gelen bir görünürlük olayı eski
  // (imleçsiz) loadMore'u çağırıp ilk sayfayı tekrar isteyebiliyordu.
  const cursorRef = useRef(null);
  const hasMoreRef = useRef(true);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const loadingRef = useRef(false);
  const sentinelRef = useRef(null);
  // Kullanıcı değişince eski isteğin cevabı yeni listeye karışmasın
  const requestUserRef = useRef(userId);

  const loadMore = useCallback(
    async (reset = false) => {
      if (loadingRef.current) return;
      if (!reset && !hasMoreRef.current) return;
      loadingRef.current = true;
      setIsLoading(true);
      setError(null);
      const forUser = userId;
      try {
        const res = await fetchPage({
          token: auth?.accessToken,
          id: userId,
          limit: PAGE_SIZE,
          ...(reset || !cursorRef.current ? {} : { before: cursorRef.current }),
        }).unwrap();
        if (requestUserRef.current !== forUser) return;
        const page = res?.data || {};
        cursorRef.current = page.nextCursor || null;
        hasMoreRef.current = !!page.hasMore;
        setItems((prev) => appendActivityPage(reset ? [] : prev, page.items || []));
        setHasMore(hasMoreRef.current);
      } catch (err) {
        if (requestUserRef.current !== forUser) return;
        setError(err?.data?.message || "Hareketler yüklenemedi");
      } finally {
        loadingRef.current = false;
        setIsLoading(false);
      }
    },
    [auth?.accessToken, fetchPage, userId]
  );

  // İlk sayfa + kullanıcı değişince sıfırla
  useEffect(() => {
    requestUserRef.current = userId;
    loadingRef.current = false;
    cursorRef.current = null;
    hasMoreRef.current = true;
    setItems([]);
    setHasMore(true);
    loadMore(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Sonsuz kaydırma: liste sonundaki işaretçi görünür olunca sonraki sayfa
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore || error || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) loadMore();
      },
      { rootMargin: "300px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, error, loadMore, items.length]);

  const groups = useMemo(() => groupActivityByDay(items), [items]);

  if (!items.length && isLoading) return <ActivitySkeleton />;

  if (!items.length && error) {
    return (
      <div className="flex flex-col items-center gap-3 py-10 text-center">
        <p className="text-sm text-gray-600">{error}</p>
        <button
          type="button"
          onClick={() => loadMore(true)}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
        >
          <FiRefreshCw className="h-4 w-4" /> Tekrar dene
        </button>
      </div>
    );
  }

  if (!items.length && !hasMore) {
    return (
      <EmptyState
        message="Henüz hareket yok"
        suggestion="Kullanıcının son 90 gündeki işlemleri burada görünür."
      />
    );
  }

  return (
    <div className="mx-auto max-w-3xl">
      {groups.map((group) => (
        <section key={group.key} className="mb-2">
          <h3 className="sticky top-0 z-20 -mx-1 mb-3 bg-white/90 px-1 py-1.5 text-xs font-SemiBold text-gray-500 backdrop-blur sm:bg-gray-50/90">
            {group.label}
          </h3>
          <ol>
            {group.items.map((item, i) => (
              <ActivityItem key={item._id} item={item} isLast={i === group.items.length - 1} />
            ))}
          </ol>
        </section>
      ))}

      <div ref={sentinelRef} className="h-1" aria-hidden="true" />
      {isLoading && (
        <p className="py-3 text-center text-xs text-gray-400 motion-safe:animate-pulse">Daha eski hareketler yükleniyor...</p>
      )}
      {error && items.length > 0 && (
        <div className="py-3 text-center">
          <button type="button" onClick={() => loadMore()} className="text-xs font-Medium text-indigo-600 hover:underline">
            Yüklenemedi — tekrar dene
          </button>
        </div>
      )}
      {!hasMore && items.length > 0 && (
        <p className="py-4 text-center text-xs text-gray-400">Son 90 günün tüm hareketleri gösterildi.</p>
      )}
    </div>
  );
}
